import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Template,
  TemplateWithFields,
  getTemplates,
  getTemplateById,
  getTemplateWithFields,
  createTemplate,
  updateTemplate,
  deleteTemplate as deleteTemplateService
} from '@/services/supabase/templates';

// Query keys
export const templateKeys = {
  all: ['templates'] as const,
  lists: () => [...templateKeys.all, 'list'] as const,
  list: (filters: any = {}) => [...templateKeys.lists(), filters] as const,
  details: () => [...templateKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...templateKeys.details(), id] as const,
};

// Get all templates
export function useTemplatesQuery(projectId?: number) {
  return useQuery({
    queryKey: projectId ? templateKeys.list({ projectId }) : templateKeys.lists(),
    queryFn: () => getTemplates(projectId),
    enabled: true,
    staleTime: 0, // No stale time for immediate updates
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}

// Get template by ID
export function useTemplateQuery(templateId: number | string | null | undefined) {
  return useQuery({
    queryKey: templateKeys.detail(templateId || 0),
    queryFn: () => getTemplateById(templateId || 0),
    enabled: !!templateId,
  });
}

// Get template with fields
export function useTemplateWithFieldsQuery(templateId: number | string | null | undefined) {
  return useQuery({
    queryKey: [...templateKeys.detail(templateId || 0), 'fields'],
    queryFn: () => getTemplateWithFields(templateId || 0),
    enabled: !!templateId,
    staleTime: 0, // No stale time for immediate updates during editing
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Always refetch when component mounts
  });
}



// Create template mutation
export function useCreateTemplateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (template: {
      name: string;
      description?: string;
      is_public?: boolean;
      project_id: number;
    }) => createTemplate(template),
    onSuccess: (_, variables) => {
      // Invalidate templates list
      queryClient.invalidateQueries({
        queryKey: templateKeys.list({ projectId: variables.project_id })
      });
      queryClient.invalidateQueries({
        queryKey: templateKeys.lists()
      });
    },
  });
}

// Update template mutation
export function useUpdateTemplateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      data
    }: {
      templateId: number | string,
      data: Partial<Template> & { project_id: number }
    }) => {
      return updateTemplate(templateId, data);
    },
    onSuccess: async (_, variables) => {
      // Force refetch specific template queries for immediate updates
      await queryClient.refetchQueries({
        queryKey: templateKeys.detail(variables.templateId)
      });

      await queryClient.refetchQueries({
        queryKey: [...templateKeys.detail(variables.templateId), 'fields']
      });

      // Force refetch templates list for immediate updates
      await queryClient.refetchQueries({
        queryKey: templateKeys.list({ projectId: variables.data.project_id })
      });
      await queryClient.refetchQueries({
        queryKey: templateKeys.lists()
      });
    },
  });
}

// Delete template mutation
export function useDeleteTemplateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, projectId }: { templateId: number | string, projectId: number }) => deleteTemplateService(templateId, projectId),
    onSuccess: (_, variables) => {
      // Remove specific template from cache
      queryClient.removeQueries({
        queryKey: templateKeys.detail(variables.templateId)
      });

      queryClient.removeQueries({
        queryKey: [...templateKeys.detail(variables.templateId), 'fields']
      });

      // Invalidate templates list
      queryClient.invalidateQueries({
        queryKey: templateKeys.list({ projectId: variables.projectId })
      });
      queryClient.invalidateQueries({
        queryKey: templateKeys.lists()
      });
    },
  });
}

// Duplicate template mutation
export function useDuplicateTemplateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ template, projectId }: { template: Template, projectId: number }) => {
      // Create a new template based on the existing one
      return createTemplate({
        name: `${template.name} (Copy)`,
        description: template.description || undefined,
        is_public: template.is_public,
        project_id: projectId
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate templates list
      queryClient.invalidateQueries({
        queryKey: templateKeys.list({ projectId: variables.projectId })
      });
      queryClient.invalidateQueries({
        queryKey: templateKeys.lists()
      });
    },
  });
}
